import { clearsEmptyProperties } from '@utils/functions/clearsEmptyProperties'
import { API_URLS } from '@services/apiRoutes'

import type {
  BranchClassificationDetailsRequestProps,
  BranchClassificationDetailsResponseProps,
  BranchClassificationRequestProps,
  BranchClassificationResponseProps,
  ConsolidatedFinancialSummaryRequestProps,
  ConsolidatedFinancialSummaryResponseProps,
  PreviewManagementFiltersRequestProps,
  PreviewManagementFiltersResponseProps,
  PreviewStatusHistoryRequestProps,
  PreviewStatusHistoryResponseProps,
  StatusPreviewBranchesRequestProps,
  StatusPreviewBranchesResponseProps,
  WeeklyStatusAutomaticSendsResponseProps,
  WeeklyStatusBranchesResponseProps,
  WeeklyStatusDetailRequestProps,
  WeeklyStatusHierarchyRequestProps,
  WeeklyStatusHierarchyResponseProps,
  WeeklyStatusRequestProps,
  WeeklyStatusResponseProps
} from './types'

import * as apiFunction from '@services/apiFunctions'

export const getPreviewManagementFilters = async (
  payload: PreviewManagementFiltersRequestProps
): Promise<PreviewManagementFiltersResponseProps> => {
  const body = clearsEmptyProperties(payload)
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.previewManagement}/filtros`,
    method: 'POST',
    body
  })
}

export const getWeeklyStatus = async (
  payload: WeeklyStatusRequestProps
): Promise<WeeklyStatusResponseProps | null> => {
  const body = clearsEmptyProperties(payload)
  const response = await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.previewManagement}/weekly-status/search`,
    method: 'POST',
    body
  })
  return response || null
}

export const getWeeklyStatusBranches = async (
  payload: WeeklyStatusDetailRequestProps
): Promise<WeeklyStatusBranchesResponseProps> => {
  const body = clearsEmptyProperties(payload)
  const response = await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.previewManagement}/weekly-status/branches/search`,
    method: 'POST',
    body
  })
  return response || { rows: [] }
}

export const getWeeklyStatusAutomaticSends = async (
  payload: WeeklyStatusDetailRequestProps
): Promise<WeeklyStatusAutomaticSendsResponseProps> => {
  const body = clearsEmptyProperties(payload)
  const response = await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.previewManagement}/weekly-status/automatic-sends/search`,
    method: 'POST',
    body
  })
  return response || { totalAccounts: 0, branches: [] }
}

export const getWeeklyStatusHierarchy = async (
  payload: WeeklyStatusHierarchyRequestProps
): Promise<WeeklyStatusHierarchyResponseProps | null> => {
  const body = clearsEmptyProperties(payload)
  const response = await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.previewManagement}/weekly-status/hierarchy/search`,
    method: 'POST',
    body
  })
  return response || null
}

export const getStatusPreviewBranches = async ({
  statusPreview,
  ...payload
}: StatusPreviewBranchesRequestProps): Promise<StatusPreviewBranchesResponseProps> => {
  const body = clearsEmptyProperties(payload)
  const response = await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.previewManagement}/weekly-status/${statusPreview}/branches/search`,
    method: 'POST',
    body
  })
  return response || { rows: [] }
}

export const getConsolidatedFinancialSummary = async (
  payload: ConsolidatedFinancialSummaryRequestProps
): Promise<ConsolidatedFinancialSummaryResponseProps | null> => {
  const body = clearsEmptyProperties(payload)
  const response = await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.previewManagement}/consolidated-financial-summary/search`,
    method: 'POST',
    body
  })
  return response || null
}

export const getBranchClassification = async (
  payload: BranchClassificationRequestProps
): Promise<BranchClassificationResponseProps | null> => {
  const body = clearsEmptyProperties(payload)
  const response = await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.previewManagement}/branch-classification/search`,
    method: 'POST',
    body
  })
  return response || null
}

export const getBranchClassificationDetails = async ({
  branchCode,
  referenceDate
}: BranchClassificationDetailsRequestProps): Promise<BranchClassificationDetailsResponseProps> => {
  return await apiFunction.apiRequestWithQueryParams({
    url: `${API_URLS.finapi.previewManagement}/branch-classification/${branchCode}/${referenceDate}/details`,
    method: 'GET'
  })
}

export const exportConsolidatedFinancialSummary = async (
  payload: ConsolidatedFinancialSummaryRequestProps
): Promise<Blob> => {
  const body = clearsEmptyProperties(payload)
  return await apiFunction.apiRequestWithBodyAndResponseType({
    url: `${API_URLS.finapi.previewManagement}/consolidated-financial-summary/export`,
    method: 'POST',
    body,
    responseType: 'blob'
  })
}

export const searchPreviewStatusHistory = async (
  payload: PreviewStatusHistoryRequestProps
): Promise<PreviewStatusHistoryResponseProps> => {
  const body = clearsEmptyProperties(payload)
  const response = await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.previewStatusHistory}/search`,
    method: 'POST',
    body
  })
  return response || { items: [] }
}

export const exportPreviewStatusHistory = async (payload: PreviewStatusHistoryRequestProps): Promise<Blob> => {
  const body = clearsEmptyProperties(payload)
  return await apiFunction.apiRequestWithBodyAndResponseType({
    url: `${API_URLS.finapi.previewStatusHistory}/export`,
    method: 'POST',
    body,
    responseType: 'blob'
  })
}

export const exportBranchClassification = async (payload: BranchClassificationRequestProps): Promise<Blob> => {
  const body = clearsEmptyProperties(payload)
  return await apiFunction.apiRequestWithBodyAndResponseType({
    url: `${API_URLS.finapi.previewManagement}/branch-classification/export`,
    method: 'POST',
    body,
    responseType: 'blob'
  })
}
